import Janus from '../external/janus.es'
import { sipRegisterStates } from './sipregisterstates.js';
import { callStateMachine } from './callstatemachine.js';

import { Machine, assign, actions, sendParent, forwardTo, send } from 'xstate';
const { escalate } = actions;


// top
const sipPluginSession = Machine({
    id: 'ipfxSipPluginSession',
    initial: 'unknown',
    context: {
        sessionService: null,
        janusSession: null,
        pluginHandle: null,
        opaqueId: null,
        instanceId: null,
        queuedDetach: false,
        masterId: null,
        registerSipserver: null,
        registerUsername: null,
        registerDisplayname: null,
        registerSecret: null,
        registerHeaders: {},
        isCallAnswered: false,
        pushToken: null,
        alertPushToken: null,
        pushPlatformName: null,
        pbxPlatform: null,
        monitorTarget: null,
        disabledPlugin: false
    },

    states: {
        inactive: {
            type: 'final'
        },

        unknown: {
            always: [
                { target: 'inactive', cond: context => context.disabledPlugin },
                { target: 'attaching', cond: context => context.instanceId === 0 },
                'waitingForMasterId'
            ],
            on: {
                DETACH: 'inactive',
            }
        },

        waitingForMasterId: {
            on: {
                ATTACH: {
                    target: 'attaching',
                    actions: assign({
                        masterId: (context, event) => event.masterId,
                        registerUsername: (context, event) => event.registerUsername
                    })
                },
                DETACH: 'inactive',
                CLEANUP: undefined,
                DETACHED: 'inactive',
            }
        },

        attaching: {
            invoke: {
                id: 'pluginAttach',
                src: 'pluginAttachService'
            },
            on: {
                ATTACHED: [
                    {
                        cond: (context, event) => context.queuedDetach,
                        actions: 'detachAction'
                    },
                    {
                        target: 'attached',
                        actions: send('DO_REGISTER'),
                        cond: context => context.instanceId !== 0
                    },
                    'attached',
                ],
                SERVICE_ERROR: {
                    target: 'inactive',
                    actions: escalate((context, event) => ({ reason: 'PLUGIN_ATTACH_FAILED', message: event.message }))
                },
                DETACH: { actions: context => context.queuedDetach = true },
                CLEANUP: undefined,
                DETACHED: 'inactive',
            }
        },

        attached: {
            invoke: {
                id: 'callControl',
                src: callStateMachine,
                data: {
                    pluginHandle: (context, event) => event.handle,
                    opaqueId: (context, event) => context.opaqueId,
                    instanceId: (context, event) => context.instanceId,
                    pbxPlatform: (context, event) => context.pbxPlatform,
                    doVideo: () => false,
                    videoSource: () => undefined,
                },
                onError: {
                    actions: [
                        (context, event) => {
                            Janus.error("callControl.OnError:, ", event.data.message);
                        },
                        sendParent((context, event) => ({
                            type: 'CALL_STATE_ERROR',
                            instanceId: context.instanceId,
                            error: event.data.message
                        }))]
                },
                onDone: 'inactive'
            },
            entry: ['onPluginStartedAction', send('DO_AUTOREGISTER')],
            on: {
                // invoke registration service
                DETACH: {
                    actions: 'detachAction',
                    actions: send((context, event) => ({ type: 'JANUS_SESSION_ENDED'}), { to: 'callControl' })
                },
                DETACHED: {
                    actions: send((context, event) => ({ type: 'JANUS_SESSION_ENDED'}), { to: 'callControl' })
                },
                SERVICE_ERROR: {
                    target: 'inactive',
                    actions: escalate((context, event) => ({ reason: 'PLUGIN_ATTACH_FAILED', message: event.message }))
                },
                // call related control events
                SIP_ERROR: { actions: forwardTo('callControl') },
                INVITE_ERROR: { actions: forwardTo('callControl') },
                DO_ENABLE_VIDEO: { actions: forwardTo('callControl') },
                DO_GET_STATS: {
                    actions: (context, event) => {
                        const pc = context.pluginHandle?.webrtcStuff?.pc
                        if (pc) {
                            OnStatsUpdate(pc, context)
                        }
                    }
                },
                DO_MAKE_CALL: { actions: forwardTo('callControl') },
                DO_HANGUP: { actions: forwardTo('callControl') },
                DO_HOLD: { actions: forwardTo('callControl') },
                DO_ICE_RESTART: { actions: forwardTo('callControl') },
                DO_RESUME: { actions: forwardTo('callControl') },
                DO_ACCEPT: { actions: forwardTo('callControl') },
                DO_DECLINE: { actions: forwardTo('callControl') },
                DO_SEND_DTMF: { actions: forwardTo('callControl') },
                DO_REJECT_REFER: { actions: forwardTo('callControl') },
                DO_BLIND_XFER: { actions: forwardTo('callControl') },
                DO_XFER: { actions: forwardTo('callControl') },
                DO_START_SR: { actions: forwardTo('callControl') },
                DO_STOP_SR: { actions: forwardTo('callControl') },
                DO_START_ON_DEMAND_SR: { 
                    actions: 'requestStartOnDemandScreenRecording'
                },
                DO_STOP_ON_DEMAND_SR: { 
                    actions: 'requestStopOnDemandScreenRecording'
                },
                RESTART_ADD_CAPTURE_ON_DEMAND_OPTION_SR: { actions: forwardTo('callControl') },
                RESTART_REMOVE_CAPTURE_ON_DEMAND_OPTION_SR: { actions: forwardTo('callControl') },
                DO_QUEUE_HOLD: { 
                    actions: 'requestQueueHold'
                },
                DO_QUEUE_HOLD_FOR: {
                    actions: 'requestQueueHoldFor'
                },
                DO_QUEUE_RESUME: {
                    actions: 'requestQueueResume'
                },
                DO_SEND_TO_VOICEMAIL: {
                    actions: 'requestSendToVoicemail'
                },
                DO_DECLINE_BUSY: {
                    actions: 'requestDeclineBusy'
                },
                DO_START_AUDIO_RECORDING: {
                    actions: 'requestStartAudioRecording'
                },
                DO_STOP_AUDIO_RECORDING: {
                    actions: 'requestStopAudioRecording'
                },
                DO_SUPPRESS_ALL_RECORDINGS: {
                    actions: 'requestSuppressAllRecordings'
                },
                DO_UNSUPPRESS_ALL_RECORDINGS: {
                    actions: 'requestUnsuppressAllRecordings'
                },
                // call states events from callControl
                CALL_STATE_IDLE: { actions: [sendParent((context, event) => event), assign({ isCallAnswered: () => false })] },
                CALL_STATE_DEEP_IDLE: { actions: [sendParent((context, event) => event), assign({ isCallAnswered: () => false })] },
                CALL_STATE_TRYING_OUT: { actions: [sendParent((context, event) => event), assign({ isCallAnswered: () => false })] },
                CALL_STATE_CALLING_OUT: { actions: [sendParent((context, event) => event), assign({ isCallAnswered: () => false })] },
                CALL_STATE_RINGING_OUT: { actions: [sendParent((context, event) => event), assign({ isCallAnswered: () => false })] },
                CALL_STATE_PROCEEDING_OUT: { actions: [sendParent((context, event) => event), assign({ isCallAnswered: () => false })] },
                CALL_STATE_PROGRESS_OUT: { actions: [sendParent((context, event) => event), assign({ isCallAnswered: () => false })] },
                CALL_STATE_CONNECTING: { actions: [sendParent((context, event) => event), assign({ isCallAnswered: () => false })] },
                CALL_STATE_CONNECTED: { actions: [sendParent((context, event) => event), assign({ isCallAnswered: () => true })] },
                CALL_STATE_HANGINGUP: { actions: [sendParent((context, event) => event), assign({ isCallAnswered: () => false })] },
                CALL_STATE_ONHOLD: { actions: [sendParent((context, event) => event), assign({ isCallAnswered: () => true })] },
                CALL_STATE_INCOMING_CALL: { actions: [sendParent((context, event) => event), assign({ isCallAnswered: () => false })] },
                CALL_STATE_DO_XFERRING: { actions: [sendParent((context, event) => event), assign({ isCallAnswered: () => true })] },
                CALL_STATE_ERROR: { actions: sendParent((context, event) => event) },
                HANDLE_REFER: { actions: sendParent((context, event) => event) },
                DO_BLIND_XFER_FAILED: { actions: sendParent((context, event) => event) },
                DO_BLIND_XFER_SUCCEEDED: { actions: sendParent((context, event) => event) },
                DO_XFER_FAILED: { actions: sendParent((context, event) => event) },
                DO_XFER_SUCCEEDED: { actions: sendParent((context, event) => event) },
                // media
                MEDIA_ON_LOCAL_STREAM: { actions: sendParent((context, event) => event) },
                MEDIA_ON_REMOTE_STREAM: { actions: sendParent((context, event) => event) },
                SLOWLINK: { actions: sendParent((context, event) => event) },                
                ON_STATS_UPDATED: { actions: sendParent((context, event) => event) },
                // SIP plugin call events
                INCOMINGCALL: { actions: forwardTo('callControl') },
                DECLINING: { actions: forwardTo('callControl') },
                DECLINED: { actions: forwardTo('callControl') },
                ACCEPTING: { actions: forwardTo('callControl') },
                CALLING: { actions: forwardTo('callControl') },
                PROCEEDING: { actions: forwardTo('callControl') },
                RINGING: { actions: forwardTo('callControl') },
                PROGRESS: { actions: forwardTo('callControl') },
                ACCEPTED: { actions: forwardTo('callControl') },
                UPDATING: { actions: forwardTo('callControl') },
                UPDATED: { actions: forwardTo('callControl') },
                TRANSFERRING: { actions: forwardTo('callControl') },
                TRANSFER: { actions: forwardTo('callControl') },
                HOLDING: { actions: forwardTo('callControl') },
                RESUMING: { actions: forwardTo('callControl') },
                DTMFSENT: { actions: forwardTo('callControl') },
                UPDATINGCALL: { actions: forwardTo('callControl') },
                HANGINGUP: { actions: forwardTo('callControl') },
                HANGUP: { actions: forwardTo('callControl') },
                CLEANUP: { actions: forwardTo('callControl') },
                NOTIFY: { actions: forwardTo('callControl'), cond: (context, event) => event.result["notify"] === "refer" },
                REQUEST_TO_RECONNECT: { actions: sendParent((context, event) => event) },
                JANUS_SESSION_ENDED: {actions: forwardTo('callControl')}
            },
            ...sipRegisterStates
        },

        detached: {
            always: 'inactive',
        }

    } // states
},
    {
        services: {
            pluginAttachService: (context, event) => (callback, onReceive) => {
                try {
                    context.pluginHandle = null;
                    context.queuedDetach = false;
                    context.janusSession.attach({
                        plugin: "janus.plugin.sip",
                        opaqueId: context.opaqueId,
                        error: (error) => callback({ type: 'SERVICE_ERROR', message: error.message }),
                        success: (pluginHandle) => callback({ type: 'ATTACHED', handle: pluginHandle }),
                        consentDialog: () => { },
                        onmessage: (msg, jsep) => OnMessage(msg, jsep, context),
                        onlocalstream: (stream) => OnLocalStream(stream, context),
                        onremotestream: (stream) => OnRemoteStream(stream, context),
                        mediaState: (medium, on) => {
                            Janus.log("Janus " + (on ? "started" : "stopped") + " receiving our " + medium);
                        },
                        slowLink: (uplink, lost) => OnSlowLink(uplink, "audio", lost, context),
                        inLinkStatus: (status, media) => OnInLinkStatus(status, media, context),
                        oncleanup: (error) => OnPluginCleanup(error, context),
                        ondetached: () => OnPluginDetached(undefined, context),
                        onreconnect: () => OnReconnectRequested(context),
                    });
                } catch (error) {
                    callback({ type: 'SERVICE_ERROR', message: error.message });
                }

            }
        },
        actions: {
            requestQueueHold: (context, event) => {
                // in-dialog request
                Janus.log("Sending message requesting to queue hold");
                let request = { request: "message", content: "Please park", uri:  undefined };
                request["headers"] = {
                    "IPFX-CTI-ADV": "queue-hold",
                    "IPFX-CTI-Opaque-ID": context.opaqueId,
                };
                context.pluginHandle.send({ "message": request });
            },
            requestQueueHoldFor: (context, event) => {
                // in-dialog request
                Janus.log("Sending message requesting to queue hold for ", event.hold_for);
                let request = { request: "message", content: "Please park for", uri:  undefined };
                request["headers"] = {
                    "IPFX-CTI-ADV": "queue-hold-for;target=" + event.hold_for,
                    "IPFX-CTI-Opaque-ID": context.opaqueId,
                };
                context.pluginHandle.send({ "message": request });
            },
            requestQueueResume: (context, event) => {
                // out-of-dialog request
                Janus.log("Sending message requesting to queue resume");
                let request = { request: "message", content: "Please retrieve", uri:  event.uri };
                request["headers"] = {
                    "IPFX-CTI-ADV": "queue-resume",
                    "IPFX-CTI-Opaque-ID": context.opaqueId,
                };
                context.pluginHandle.send({ "message": request });
            },            
            requestSendToVoicemail: (context, event) => {
                // in-dialog request
                Janus.log("Sending message requesting to send call to voicemail");
                let request = { request: "message", content: "To voicemail please", uri:  undefined };
                request["headers"] = {
                    "IPFX-CTI-ADV": "send-to-voicemail",
                    "IPFX-CTI-Opaque-ID": context.opaqueId,
                };
                context.pluginHandle.send({ "message": request });
            },
            requestDeclineBusy: (context, event) => {
                // in-dialog request
                Janus.log("Sending message requesting to decline call with busy");
                let request = { request: "message", content: "I'm busy", uri:  undefined };
                request["headers"] = {
                    "IPFX-CTI-ADV": "decline-busy",
                    "IPFX-CTI-Opaque-ID": context.opaqueId,
                };
                context.pluginHandle.send({ "message": request });
            },
            requestStartAudioRecording: (context, event) => {
                // in-dialog request
                Janus.log("Sending message requesting to start audio recording");
                let request = { request: "message", content: "Let's start recording", uri:  undefined };
                request["headers"] = {
                    "IPFX-CTI-ADV": "start-adhoc-audio-recording",
                    "IPFX-CTI-Opaque-ID": context.opaqueId,
                };
                context.pluginHandle.send({ "message": request });
            },
            requestStopAudioRecording: (context, event) => {
                // in-dialog request
                Janus.log("Sending message requesting to stop audio recording");
                let request = { request: "message", content: "Let's stop recording", uri:  undefined };
                request["headers"] = {
                    "IPFX-CTI-ADV": "stop-adhoc-audio-recording",
                    "IPFX-CTI-Opaque-ID": context.opaqueId,
                };
                context.pluginHandle.send({ "message": request });
            },
            requestSuppressAllRecordings: (context, event) => {
                // in-dialog request
                Janus.log("Sending message requesting to suppress audio recording");
                let request = { request: "message", content: "Must suppress recording", uri:  event.uri };
                request["headers"] = {
                    "IPFX-CTI-ADV": "suppress-all-recordings",
                    "IPFX-CTI-Opaque-ID": context.opaqueId,
                };
                context.pluginHandle.send({ "message": request });
            },
            requestUnsuppressAllRecordings: (context, event) => {
                // in-dialog request
                Janus.log("Sending message requesting to unsuppress audio recording");
                let request = { request: "message", content: "Must unsuppress recording", uri:  event.uri };
                request["headers"] = {
                    "IPFX-CTI-ADV": "unsuppress-all-recordings",
                    "IPFX-CTI-Opaque-ID": context.opaqueId,
                };
                context.pluginHandle.send({ "message": request });
            },
            requestStartOnDemandScreenRecording: (context, event) => {
                Janus.log("Sending message requesting to start on-demand SR to ", event.uri, ", opaque-id: ", context.opaqueId);
                let request = { request: "message", content: "Please hold", uri:  event.uri };
                let hvalue = "true;tag=" 
                    + (event.srOnDemandRecordingTag ? ("\"" + event.srOnDemandRecordingTag  + "\"") : "none")
                    + ";record-audio=" 
                    + (event.srRecordAudio ? "true" : "false");
                request["headers"] = {
                    "IPFX-ODSR": hvalue,
                    "IPFX-CTI-Opaque-ID": context.opaqueId,
                };
                context.pluginHandle.send({ "message": request });
            },
            requestStopOnDemandScreenRecording: (context, event) => {
                Janus.log("Sending message requesting to stop on-demand SR to ", event.uri, ", opaque-id: ", context.opaqueId);
                let request = { request: "message", content: "I'm here to help", uri:  event.uri  };
                request["headers"] = {
                    "IPFX-ODSR": "false",
                    "IPFX-CTI-Opaque-ID": context.opaqueId,
                };
                context.pluginHandle.send({ "message": request });
            },
            detachAction: (context, event) => {
                if (context.pluginHandle !== undefined) {
                    context.pluginHandle.detach();
                }
            },
            onPluginStartedAction: (context, event) => {
                var this_plugin;
                for (let value of context.sessionService.children.values()) {
                    if (value.state.context.instanceId == context.instanceId) {
                        this_plugin = value;
                        break;
                    }
                }

                if (this_plugin.state.children.callControl !== undefined && this_plugin.state.children.callControl.listeners.size < 1) {
                    this_plugin.state.children.callControl.listeners.add(state => {
                        if (state.history === undefined)
                            Janus.log("Machine callControl(", context.instanceId, ") state change: ",
                                state.history,
                                "-->",
                                state.value,
                                ", event: ",
                                state.event);
                        else
                            Janus.log("Machine callControl(", context.instanceId, ") state change: ",
                                state.history.value,
                                "-->",
                                state.value,
                                ", event: ",
                                state.event);
                    });
                }
                context.pluginHandle = event.handle;
            },
        }
    }
);

//function ForwardEventToServiceInstance(context, event) {
//	for (let value of sessionService.children.values()) {
//		if (value.machine.config.id === "ipfxSipPluginSession" && value.machine.context.opaqueId === context.opaqueId) {
//            value.send(event);
//        }
//	}
//}

function ForwardEventToServiceInstance(context, event, serviceId, service) {
    if (service.machine === undefined) {
        return false;
    }
    if (service.machine.config.id === serviceId && service.machine.context.instanceId == context.instanceId) {
        try {
            service.send(event);
        } catch (err) {
            Janus.warn('Failed to handle event', event, ", reason:", err.message)
        }
        return true;
    }
    for (let value of service.children.values()) {
        if (ForwardEventToServiceInstance(context, event, serviceId, value)) {
            return true;
        }
    }
    return false;
}

function OnRemoteStream(stream, context) {
    try {
        ForwardEventToServiceInstance(context, { type: 'MEDIA_ON_REMOTE_STREAM', instanceId: context.instanceId, stream: stream }, "ipfxSipPluginSession", context.sessionService);
    } catch (err) {
        Janus.error("OnRemoteStream: ", err);
        ForwardEventToServiceInstance(context, { type: 'SERVICE_ERROR', message: err.message }, "ipfxSipPluginSession", context.sessionService);
    }
}




function OnLocalStream(stream, context) {
    try {
        ForwardEventToServiceInstance(context, { type: 'MEDIA_ON_LOCAL_STREAM', instanceId: context.instanceId, stream: stream }, "ipfxSipPluginSession", context.sessionService);
    } catch (err) {
        Janus.error("OnLocalStream: ", err);
        ForwardEventToServiceInstance(context, { type: 'SERVICE_ERROR', message: err.message }, "ipfxSipPluginSession", context.sessionService);
    }
}



function OnMessage(msg, jsep, context) {
    try {
        Janus.debug(" ::: Got a message :::");
        Janus.debug(msg);
        // check for errors
        var error = msg["error"];
        if (error !== null && error !== undefined) {
            ForwardEventToServiceInstance(context, { type: 'SIP_ERROR', message: error, error_code: msg["error_code"] }, "ipfxSipPluginSession", context.sessionService);
            return;
        }
        var result = msg["result"];
        if (result !== null && result !== undefined && result["event"] !== undefined && result["event"] !== null) {
            var sip_event = result["event"];
            ForwardEventToServiceInstance(context, { type: sip_event.toUpperCase(), jsep: jsep, result: msg["result"], call_id: msg["call_id"] }, "ipfxSipPluginSession", context.sessionService);
        } else {
            Janus.warn("OnMessage: random message!")
        }
    } catch (err) {
        Janus.error("OnMessage: ", err);
        ForwardEventToServiceInstance(context, { type: 'SERVICE_ERROR', message: err.message }, "ipfxSipPluginSession", context.sessionService);
    }
}

function OnPluginCleanup(error, context) {
    ForwardEventToServiceInstance(context, { type: 'CLEANUP', message: error }, "ipfxSipPluginSession", context.sessionService);
}

function OnPluginDetached(error, context) {
    ForwardEventToServiceInstance(context, { type: 'DETACHED', message: error }, "ipfxSipPluginSession", context.sessionService);
}

function OnSlowLink(uplink, media, lost, context) {
    ForwardEventToServiceInstance(context, { type: 'SLOWLINK', instanceId: context.instanceId, uplink: uplink, media: media, lost: lost }, "ipfxSipPluginSession", context.sessionService);
}

function OnInLinkStatus(status, media, context) {
    if (!status && media == "audio") {
        ForwardEventToServiceInstance(context, { type: 'DO_ICE_RESTART', instanceId: context.instanceId}, "ipfxSipPluginSession", context.sessionService);
    }
}


function OnReconnectRequested(context) {
    if (context.instanceId === 0) {
        ForwardEventToServiceInstance(context, { type: 'REQUEST_TO_RECONNECT', instanceId: context.instanceId }, "ipfxSipPluginSession", context.sessionService);
    }
}


async function OnStatsUpdate(pc, context) {
    const stats = await pc.getStats(null)
    ForwardEventToServiceInstance(context, {
        type: 'ON_STATS_UPDATED',
        instanceId: context.instanceId,
        stats
    }, "ipfxSipPluginSession", context.sessionService)
}

export { sipPluginSession };